import { useState, useEffect } from 'react'
import { Header } from '@/components/header'
import { Footer } from '@/components/footer'
import { Helmet } from 'react-helmet-async'

export default function Contact() {
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  // State required for the contact form
  const [contactForm, setContactForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    message: ''
  })
  
  const [contactSubmitting, setContactSubmitting] = useState(false)
  const [contactSubmitted, setContactSubmitted] = useState(false)
  const [contactError, setContactError] = useState('')

  const handleContactChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setContactForm(prev => ({ ...prev, [name]: value }))
  }

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setContactSubmitting(true)
    setContactError('')
    
    // Simulate API submission
    try {
      setTimeout(() => {
        setContactSubmitting(false)
        setContactSubmitted(true)
        setContactForm({ firstName: '', lastName: '', email: '', phone: '', message: '' })
        
        // Clear success message after 5 seconds
        setTimeout(() => setContactSubmitted(false), 5000)
      }, 1500)
    } catch (err) {
      setContactError('Something went wrong. Please try again.')
      setContactSubmitting(false)
    }
  }

  // Schema data for the Contact Page
  const schemaData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "ContactPage",
        "@id": "https://busybeaverconstruction.com/contact",
        "url": "https://busybeaverconstruction.com/contact",
        "name": "Contact Us | Busy Beaver Construction",
        "description": "Contact Busy Beaver Construction for a free estimate on your next deck, pergola, fence, or outdoor living project in Calgary. Call (403) 286-5400.",
        "mainEntity": {
          "@type": "LocalBusiness",
          "@id": "https://busybeaverconstruction.com/#organization",
          "name": "Busy Beaver Construction",
          "url": "https://busybeaverconstruction.com/",
          "logo": "https://busybeaverconstruction.com/assets/images/2020/03/busy-beaver-logo.png",
          "telephone": "+1-403-286-5400",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "62 Silverado Ranch Way SW",
            "addressLocality": "Calgary",
            "addressRegion": "AB",
            "addressCountry": "CA"
          },
          "contactPoint": {
            "@type": "ContactPoint",
            "telephone": "+1-403-286-5400",
            "contactType": "customer service",
            "areaServed": "Calgary"
          }
        }
      },
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": "https://busybeaverconstruction.com/"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Contact",
            "item": "https://busybeaverconstruction.com/contact"
          }
        ]
      }
    ]
  };

  return (
    <main className="flex flex-col min-h-screen">
      <Helmet>
        <title>Contact Us | Free Estimates | Busy Beaver Construction Calgary</title>
        <meta name="description" content="Contact Busy Beaver Construction for a free estimate on your next deck, pergola, fence, or outdoor living project in Calgary. Call (403) 286-5400." />
        <link rel="canonical" href="https://busybeaverconstruction.com/contact" />
      </Helmet>

      {/* JSON-LD Schema placed OUTSIDE Helmet to ensure rendering */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }} />

      <Header />

      {/* Hero Section */}
      <section className="relative h-[400px] flex items-center justify-center">
        <div className="absolute inset-0 bg-black/60 z-10"></div>
        <img 
          src="/assets/images/Decks/Wood Decks/Large-outdoor-seating-area-with-Custom-Cedar-privacy-wall.jpg"
          alt="Contact Busy Beaver Construction"
          className="absolute inset-0 w-full h-full object-cover"
          fetchPriority="high"
        />
        <div className="relative z-20 mt-24 text-center max-w-4xl mx-auto px-4">
          <div className="inline-block bg-beaver-green rounded py-2 px-6 mb-6">
            <p className="text-black text-sm font-bold tracking-wider uppercase">Get in Touch</p>
          </div>
          <h1 className="text-white text-5xl lg:text-7xl font-primary drop-shadow-lg leading-tight text-white">
            Contact Us
          </h1>
        </div>
      </section>

      {/* Main Content (Dark Theme to match the provided form) */}
      <section className="py-16 md:py-24 bg-white flex-grow">
        <div className="container mx-auto px-4 max-w-6xl">
          
          <div className="mb-12 text-center text-gray-900">
            <p className="text-gray-500 font-text text-lg">
              Fill out the form below to discuss your next deck, fence, or outdoor living project.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start max-w-4xl mx-auto">
            {/* Column 1: Contact Form */}
            <form className="space-y-3 w-full" onSubmit={handleContactSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <div>
                  <div className="py-2">
                    <input 
                      type="text" 
                      placeholder="First Name"
                      name="firstName"
                      value={contactForm.firstName}
                      onChange={handleContactChange}
                      required
                      className="w-full px-3 py-2 bg-[#222222] border border-white/40 text-white text-sm font-text"
                    />
                  </div>
                  <div className="py-2">
                    <input 
                      type="email" 
                      placeholder="Email"
                      name="email"
                      value={contactForm.email}
                      onChange={handleContactChange}
                      required
                      className="w-full px-3 py-2 bg-[#222222] border border-white/40 text-white text-sm font-text"
                    />
                  </div>
                </div>

                <div>
                  <div className="py-2">
                    <input
                      type="text"
                      placeholder="Last Name"
                      name="lastName"
                      value={contactForm.lastName}
                      onChange={handleContactChange}
                      required
                      className="w-full px-3 py-2 bg-[#222222] border border-white/40 text-white text-sm font-text"
                    />
                  </div>
                  <div className="py-2">
                    <input
                      type="tel"
                      placeholder="Phone Number"
                      name="phone"
                      value={contactForm.phone}
                      onChange={handleContactChange}
                      required
                      className="w-full px-3 py-2 bg-[#222222] border border-white/40 text-white text-sm font-text"
                    />
                  </div>
                </div>
              </div>
              <div className="py-2">
                <textarea 
                  placeholder="Message" 
                  rows={4}
                  name="message"
                  value={contactForm.message}
                  onChange={handleContactChange}
                  required
                  className="w-full px-3 py-2 bg-[#222222] border border-white/40 text-white text-sm font-text"
                ></textarea>
              </div>
            
              <div className="relative pt-2 flex items-center justify-between">
                <div>
                  {contactSubmitting ? (
                    <p className="text-beaver-green font-bold">Sending your message...</p>
                  ) : contactSubmitted ? (
                    <p className="text-beaver-green font-bold">Message sent successfully!</p>
                  ) : contactError ? (
                    <p className="text-red-500 font-bold">{contactError}</p>
                  ) : null}
                </div>
                <div className="text-right md:mt-0">
                  <button 
                    type="submit" 
                    disabled={contactSubmitting}
                    className="bg-[#86efac] hover:brightness-110 transition-all duration-300 text-black px-6 py-3 text-sm font-bold uppercase rounded font-accent disabled:opacity-50"
                  >
                    Send Message
                  </button>
                </div>
              </div>
            </form>

            {/* Column 2: NAP & Google Map */}
            <div className="w-full h-full flex flex-col space-y-6 mt-4">
              <div className="font-text text-gray-500 space-y-4">
                <p className="flex items-center gap-3">
                  <i className="fas fa-map-marker-alt text-[#86efac] w-5"></i> 
                  62 Silverado Ranch Way SW, Calgary, AB
                </p>
                <p className="flex items-center gap-3">
                  <i className="fas fa-phone-alt text-[#86efac] w-5"></i> 
                  <a href="tel:+14032865400" className="hover:text-beaver-green transition-colors"
                     onClick={() => {
                       if (typeof window !== 'undefined') {
                         (window as any).dataLayer = (window as any).dataLayer || [];
                         (window as any).dataLayer.push({
                           event: 'phone_click'
                         });
                       }
                     }}
                  >(403) 286-5400</a>
                </p>
              </div>
              
              <div className="w-full flex-grow min-h-[250px] bg-gray-200 rounded overflow-hidden border border-gray-300">
                <iframe 
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2516.8901203433024!2d-114.08861689999999!3d50.88874399999999!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x53716ee60e8ebd71%3A0x6da301a0d492fb2b!2sBusy%20Beaver%20Construction%20Ltd.!5e0!3m2!1sen!2sin!4v1782881294696!5m2!1sen!2sin" 
                  width="100%" 
                  height="100%" 
                  style={{ border: 0 }} 
                  allowFullScreen={false} 
                  loading="lazy" 
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Busy Beaver Construction Service Area Calgary"
                ></iframe>
              </div>
            </div>
          </div>

        </div>
      </section>

      <Footer />
    </main>
  )
}